/**
 * Chat view (Task 201 extraction). The whole idle ⇄ chat-active render tree that
 * App.tsx used to inline: topbar (idle/chat variant), idle scene (hero + composer
 * + seeds + footer), chat scene (sidebar + thread + dock + source rail), and the
 * morph overlay. Presentational — it consumes the `useChat` result and the
 * `useComposerPrefill` result; all state/handlers live in those hooks.
 *
 * Visual structure (per docs/public-chat-design-handoff.md):
 *   - idle:        topbar lang toggle · centered kicker/title/sub · composer · seeds
 *   - submitting / motion phases: morph overlay over the idle (or fading-out idle)
 *   - chat-active: topbar wordmark + "New" · scroll thread · docked composer
 */
import type { ChangeEvent, RefObject } from "react";
import { Composer } from "../components/Composer";
import { AboutDialog } from "../components/AboutDialog";
import { Thread } from "../components/Thread";
import { ErrorBanner } from "../components/ErrorBanner";
import { IndexingNotice } from "../components/IndexingNotice";
import { useLatestBook } from "./use-latest-book";
import { useRelatedPassages } from "./use-related-passages";
import { isEmbedded } from "../embed/embed-mode";
import { MorphAnimation } from "../components/MorphAnimation";
import { SourceRail } from "../components/SourceRail";
import { Sidebar } from "../components/Sidebar";
import { Topbar } from "../components/Topbar";
import { SharedBadge } from "../components/SharedBadge";
import { PopularQuestions } from "../components/PopularQuestions";
import { usePopularTopics } from "./use-popular-topics";
import type { Strings, Language } from "../strings";
import { MAX_QUERY_LENGTH } from "./answer-config";
import type { UseChat } from "./use-chat";
import type { ComposerPrefill } from "./use-composer-prefill";

interface ChatViewProps {
  chat: UseChat;
  prefill: ComposerPrefill;
  strings: Strings;
  language: Language;
  onLanguageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  reducedMotion: boolean;
  onAbout: () => void;
  onCorpus: () => void;
  onOpenBook: (book: string, chapter: number) => void;
  aboutRef: RefObject<HTMLDialogElement>;
}

export function ChatView(props: ChatViewProps): JSX.Element {
  const { chat, prefill, strings, language, onLanguageChange, reducedMotion, onAbout, onCorpus, onOpenBook, aboutRef } = props;
  const { pendingAsk, askNonce, prefillShared } = prefill;
  const popularTopics = usePopularTopics();
  const latestBook = useLatestBook();
  const related = useRelatedPassages();
  // Embedded in a host page (Koresh's scripture reader): strip our own chrome.
  const embedded = isEmbedded();
  const {
    thread,
    pending,
    activeQ,
    loading,
    started,
    errored,
    banner,
    latest,
    citedSourceIndex,
    composerDisabled,
    idleCharging,
    idleDim,
    idleLoadingReduced,
    showMorph,
    morphPhase,
    morphDirection,
    morphGeometry,
    composerRef,
    holdToRelease,
    sendQuery,
    handleStop,
    handleRetry,
    handleDismiss,
    handleNewChat,
    handleCite,
    handlePhaseComplete,
  } = chat;

  return (
    <div className={`app${embedded ? " app--embed" : ""}`}>
      {embedded ? null : started ? (
        <Topbar
          variant="chat"
          active="chat"
          strings={strings}
          language={language}
          onLanguageChange={onLanguageChange}
          onAbout={onAbout}
          onNewChat={handleNewChat}
        />
      ) : (
        <Topbar
          variant="idle"
          active="chat"
          strings={strings}
          language={language}
          onLanguageChange={onLanguageChange}
          onAbout={onAbout}
        />
      )}

      {!started ? (
        <div
          className={`idle${idleDim ? " idle--dim" : ""}${
            idleCharging ? " idle--charging" : ""
          }`}
        >
          {embedded ? null : (
            <div className="idle__head">
              <p className="idle__kicker">{strings.idleKicker}</p>
              <h1 className="idle__title">{strings.idleTitle}</h1>
              <p className="idle__sub">{strings.idleSub}</p>
            </div>
          )}
          {errored && banner !== null ? (
            <ErrorBanner
              strings={strings}
              error={banner}
              onRetry={handleRetry}
              onDismiss={handleDismiss}
            />
          ) : null}
          <SharedBadge strings={strings} visible={prefillShared && pendingAsk !== null} />
          <Composer
            key={pendingAsk !== null ? `idle-ask-${askNonce}` : "idle"}
            ref={composerRef}
            strings={strings}
            disabled={composerDisabled}
            maxLength={MAX_QUERY_LENGTH}
            placement="idle"
            holdToRelease={holdToRelease}
            initialText={pendingAsk ?? undefined}
            onSubmit={(text) => {
              void sendQuery(text);
            }}
          />
          {embedded ? null : idleLoadingReduced ? (
            <p className="idle__loading" role="status" aria-live="polite">
              {strings.arrowLoadingLabelReduced}
            </p>
          ) : (
            // The suggestion strip stays MOUNTED through drawing + landing so
            // the centered column never re-flows and the composer holds its
            // position under the fixed trace/arrow overlay (Task 074). It
            // recedes visually via .idle--charging .popular-questions, not by
            // unmounting. Questions are ranked by real demand (Task 244), with
            // a curated fallback when analytics are thin/unreachable.
            <PopularQuestions
              language={language}
              topics={popularTopics}
              onPick={(question) => void sendQuery(question)}
            />
          )}
          {/* Task 174/189: a quiet home footer with the official congregation
              link (new tab). Biblioteca/Conexiones live in the unified topbar
              (Task 224 — explore cards removed per Diego, the nav suffices). */}
          {embedded ? null : (
            <>
              <footer className="idle__footer">
                <a
                  className="idle__footer-link"
                  href="https://losdelcamino.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {strings.navOfficialSite}
                </a>
              </footer>
              <IndexingNotice strings={strings} latestBook={latestBook} onOpenBook={onOpenBook} />
            </>
          )}
        </div>
      ) : (
        <div className="chat" data-testid="chat-shell">
          {embedded ? null : (
            <Sidebar
              strings={strings}
              onNewChat={handleNewChat}
              onAbout={onAbout}
              onCorpus={onCorpus}
            />
          )}
          <div className="chat-main">
            <Thread
              strings={strings}
              language={language}
              thread={thread}
              pendingQuery={pending}
              pendingLabel={
                reducedMotion ? strings.arrowLoadingLabelReduced : strings.arrowLoadingLabel
              }
              sceneDirection={morphDirection}
              onCite={handleCite}
              related={related}
              onAskRelated={(question) => void sendQuery(question)}
            />
            <div className="dock">
              <div className="dock__inner">
                {errored && banner !== null ? (
                  <ErrorBanner
                    strings={strings}
                    error={banner}
                    onRetry={handleRetry}
                    onDismiss={handleDismiss}
                  />
                ) : null}
                <SharedBadge strings={strings} visible={prefillShared && pendingAsk !== null} />
                <Composer
                  key={pendingAsk !== null ? `active-ask-${askNonce}` : "active"}
                  ref={composerRef}
                  strings={strings}
                  disabled={composerDisabled}
                  maxLength={MAX_QUERY_LENGTH}
                  placement="dock"
                  placeholderOverride={strings.composerPlaceholderDock}
                  revealDirection={morphDirection}
                  holdToRelease={holdToRelease}
                  busy={loading}
                  onStop={handleStop}
                  initialText={pendingAsk ?? undefined}
                  onSubmit={(text) => {
                    void sendQuery(text);
                  }}
                />
              </div>
            </div>
          </div>
          <SourceRail
            strings={strings}
            loading={activeQ !== null}
            sources={latest !== null ? latest.sources : null}
            subtitleQuery={
              activeQ !== null ? activeQ : latest !== null ? latest.query : null
            }
            highlightedIndex={citedSourceIndex}
          />
        </div>
      )}

      {showMorph ? (
        <MorphAnimation
          phase={morphPhase}
          direction={morphDirection}
          reducedMotion={reducedMotion}
          geometry={morphGeometry}
          onPhaseComplete={handlePhaseComplete}
        />
      ) : null}

      <AboutDialog strings={strings} dialogRef={aboutRef} />
    </div>
  );
}
